/**
 * "Credited experience" on an Applicants-tab card — the experience items tied
 * to this applicant (`item.associations.borrowers`), the same list the General
 * Contractors roster shows per contractor. Rendered only when the loan uses
 * the experience workbook AND at least one item credits this person; the tab
 * owns the workbook fetch and the item dialog, so a click here opens the same
 * editor the Experience tab uses.
 *
 * Folds from a CollapsibleSectionHeader, like "Prior loans & servicing"; folded,
 * it keeps "N items · M verified" in the summary pill.
 */
import { Box, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";

import Flx from "../../../../../components/layout/Flx";
import { status } from "../../../../../theme/colors";
import ExperienceItemsGrid from "../../../experience/ExperienceItemsGrid";
import { fmtPoints, itemPoints, needsAttention } from "../../../experience/experienceConstants";
import CollapsibleSectionHeader, {
  SummaryPill,
} from "../../../shared/CollapsibleSectionHeader";

const stop = (e) => e.stopPropagation();

const ApplicantCreditedExperience = ({
  items,
  loan,
  partyById,
  activeItemId,
  onOpenItem,
}) => {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;

  const verified = items.filter((it) => it?.verified).length;
  const flagged = items.some((it) => needsAttention(it));
  const points = items
    .filter((it) => it?.verified)
    .reduce((sum, it) => sum + (Number(itemPoints(it)) || 0), 0);
  const summaryText = `${items.length} item${items.length === 1 ? "" : "s"} · ${verified} verified${
    verified ? ` · ${fmtPoints(points)} pts` : ""
  }`;

  return (
    <Box onClick={stop} onMouseDown={stop} sx={{ cursor: "default" }}>
      <Flx ac g={1} sx={{ mb: open ? 1.25 : 0 }}>
        {/* The count and the flag ride inside the header so the whole row
            stays one click target. */}
        <CollapsibleSectionHeader
          title="Credited experience"
          open={open}
          onToggle={() => setOpen((v) => !v)}
          meta={
            <>
              <SummaryPill>{summaryText}</SummaryPill>
              {flagged && (
                <Tooltip title="An item still has no parent or no proof — it can't persist until that is fixed.">
                  <Box
                    sx={{
                      ml: 0.75,
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: status.warning.solid,
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}
            </>
          }
        />
      </Flx>
      {open && (
        <ExperienceItemsGrid
          items={items}
          loan={loan}
          byId={partyById}
          activeItemId={activeItemId}
          onRowClick={onOpenItem}
        />
      )}
    </Box>
  );
};

ApplicantCreditedExperience.propTypes = {
  items: PropTypes.array,
  loan: PropTypes.object,
  partyById: PropTypes.instanceOf(Map),
  activeItemId: PropTypes.string,
  onOpenItem: PropTypes.func,
};

export default ApplicantCreditedExperience;
